export const PILLAR_LABELS = {
  websitePerformance: 'Website Performance',
  seoLocalVisibility: 'SEO + Local Visibility',
  conversionUx: 'Conversion UX',
  reputation: 'Reputation',
  brandSocial: 'Brand / Social'
};

export const formatGap = (value, decimals = 0) => {
  const number = Number(value || 0);
  const rounded = decimals > 0 ? number.toFixed(decimals) : String(Math.round(number));
  return `${number >= 0 ? '+' : ''}${rounded}`;
};

export const getScoreTone = (score) => {
  const value = Number(score || 0);
  if (value < 45) return 'critical';
  if (value < 65) return 'warning';
  if (value < 80) return 'fair';
  return 'strong';
};

export const getImpactTone = (value) => {
  const impact = String(value || '').toLowerCase();
  if (impact.includes('high')) return 'critical';
  if (impact.includes('medium')) return 'warning';
  return 'fair';
};

export const getDeltaTone = (delta) => {
  const value = Number(delta || 0);
  if (value <= -15) return 'critical';
  if (value <= -5) return 'warning';
  if (value < 3) return 'fair';
  return 'strong';
};

export const formatDelta = (delta) => {
  const value = Number(delta || 0);
  const sign = value > 0 ? '+' : '';
  const arrow = value > 0 ? '↑' : value < 0 ? '↓' : '→';
  return `${arrow} ${sign}${value}`;
};

export const getModuleFailedChecks = (module, limit = 3) => {
  const checks = Array.isArray(module?.checks) ? module.checks : [];
  return checks.filter((item) => !item?.pass).slice(0, limit);
};

export const getModuleDetailLines = (module) => {
  const evidence = module?.evidence || {};

  if (module?.key === 'googleBusinessProfileDeepDive') {
    const website = evidence?.website || {};
    const market = evidence?.marketPosition || {};
    const reviewSignals = evidence?.reviewSignals || {};
    const intensive = evidence?.intensiveSearch || {};
    const topThemes = Array.isArray(reviewSignals?.topThemes)
      ? reviewSignals.topThemes.map((item) => `${item.theme} (${item.count})`).slice(0, 3).join(', ')
      : '';
    const querySample = Array.isArray(intensive?.queries)
      ? intensive.queries.slice(0, 3).join(' • ')
      : '';

    return [
      `Website: ${website.validDomain ? 'valid domain' : 'invalid domain'} · ${website.reachable ? 'reachable' : 'not reachable'}${website.status ? ` (${website.status})` : ''}`,
      `Market percentile: rating ${market.ratingPercentile || 0}th · reviews ${market.reviewPercentile || 0}th · photos ${market.photoPercentile || 0}th`,
      `Gap to top local avg: rating ${formatGap(market.ratingGapToTopAvg, 2)} · reviews ${formatGap(market.reviewGapToTopAvg)} · photos ${formatGap(market.photoGapToTopAvg)}`,
      `Review freshness: ${reviewSignals.recentReviewCount180d || 0} in last 180 days · median age ${reviewSignals.medianReviewAgeDays || 0} days${topThemes ? ` · themes: ${topThemes}` : ''}`,
      `Intensive search matrix: ${intensive.queryCount || 0} query patterns${querySample ? ` · sample: ${querySample}` : ''}`
    ];
  }

  if (module?.key === 'websiteAudit') {
    return [
      `Website status: ${evidence?.reachable ? 'reachable' : 'unreachable'}${evidence?.status ? ` (${evidence.status})` : ''}`,
      `Pages crawled: ${evidence?.pageCount || 0}`
    ];
  }

  if (module?.key === 'mobileExperience') {
    return [
      `Reachable during mobile scan: ${evidence?.reachable ? 'yes' : 'no'}`,
      `Viewport tag detected: ${evidence?.viewport ? 'yes' : 'no'}`,
      `Tap-to-call link detected: ${evidence?.telLink ? 'yes' : 'no'}`,
      `Pages checked: ${evidence?.pageCount || 0}`
    ];
  }

  if (module?.key === 'googleReviewSentiment') {
    const trend = evidence?.trend || {};
    const topThemeGaps = Array.isArray(evidence?.competitorThemeGap)
      ? evidence.competitorThemeGap.slice(0, 2).map((item) => `${item.theme} (${Math.round((item.gapVsCompetitorNegativeRate || 0) * 100)}pp gap)`).join(', ')
      : '';
    const topFixes = Array.isArray(module?.ai?.deepDive?.priorityFixes) && module.ai.deepDive.priorityFixes.length > 0
      ? module.ai.deepDive.priorityFixes.slice(0, 2).join(' | ')
      : Array.isArray(evidence?.priorityFixes)
        ? evidence.priorityFixes.slice(0, 2).join(' | ')
        : '';
    const rootCauses = Array.isArray(module?.ai?.deepDive?.rootCauses)
      ? module.ai.deepDive.rootCauses.slice(0, 2).join(' | ')
      : '';
    const criticalExamples = Array.isArray(evidence?.criticalIssues) && evidence.criticalIssues.length > 0
      ? evidence.criticalIssues.slice(0, 2).map((item) => `${item.term} (${item.rating}★)`).join(' | ')
      : '';

    return [
      `Review samples analyzed: ${evidence?.sampleCount || 0}`,
      `Positive vs negative keyword hits: ${evidence?.positiveHits || 0} vs ${evidence?.negativeHits || 0}`,
      `Average snippet rating: ${evidence?.averageSnippetRating || 0} vs competitor avg ${evidence?.competitorRatingAvg || 0}`,
      `Severity profile: critical ${evidence?.severity?.critical || 0} · major ${evidence?.severity?.major || 0} · minor ${evidence?.severity?.minor || 0}`,
      `Trend: ${trend?.direction || 'stable'} (${trend?.momentum || 0} pts) · 30d ${trend?.window30?.score || 50} · 90d ${trend?.window90?.score || 50} · 180d ${trend?.window180?.score || 50}`,
      criticalExamples ? `Critical issue examples: ${criticalExamples}` : '',
      topThemeGaps ? `Largest competitor theme gaps: ${topThemeGaps}` : '',
      rootCauses ? `AI root causes: ${rootCauses}` : '',
      topFixes ? `Priority fixes: ${topFixes}` : ''
    ].filter(Boolean);
  }

  if (module?.key === 'photoQualityQuantity') {
    return [
      `Photo count: ${evidence?.photoCount || 0}`,
      `Local comparison: avg ${evidence?.competitorPhotoAvg || 0}, median ${evidence?.competitorPhotoMedian || 0}`,
      `Photo density index: ${evidence?.densityIndex || 0}`
    ];
  }

  if (module?.key === 'googleBusinessProfile') {
    return [
      `Reviews: ${evidence?.reviews || 0} vs local avg ${evidence?.competitorReviewAvg || 0}`,
      `Photos: ${evidence?.photoCount || 0} vs local avg ${evidence?.competitorPhotoAvg || 0}`,
      `Category depth: ${evidence?.categoryDepth || 0}`
    ];
  }

  return [];
};